import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import {
  AppService,
  AuthService,
  BikeRoute,
  CommutingTrip,
  CommutingTripService,
  Employee,
  EmployeeService,
  EquipmentService,
  Event,
  EventService,
  Geoagglo,
  GeocoderService,
  Geogroup,
  GeogroupService,
  HttpService,
  IBounds,
  IReportTypesMapValue,
  IUserGameLevel,
  Partner,
  Place,
  PoiCategory,
  ReportSource,
  ReportType,
  Ride,
  RideService,
  RideTheme,
  Route,
  RouteService,
  TBaseLayer,
  TBicycle,
  TPoiCategoryCode,
  TReportTypeCode,
  TReportTypeCodeWithOthers,
  Trip,
  TripService,
  User,
  UserGeogroup,
  UserPlace,
  UserRide,
  UserService,
  reportTypesMap as _reportTypesMap,
  countryCodes,
  poiCategoryCodes,
  reportParentsCodes,
  reportTypeOthers,
  reportTypesChildren,
  reportTypesTitleKeys,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { SvgIconProps } from '@mui/material';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { graphql, useStaticQuery } from 'gatsby';
import { LngLatBoundsLike, Map, PaddingOptions } from 'maplibre-gl';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { clearQueryParam } from 'react-use-query-param-string';

import { ContextQuery, PoiCategoryData } from '../graphql-types';

import { HideMapIcon } from './components/icons';
import environment from './environment';
import { TH3Cells } from './hooks/map/heatmap';
import useQueryParams from './hooks/query-params';
import useTracker from './hooks/tracker';
import { parseGeoagglo } from './utils/geoagglo';
import { parsePoiCategories } from './utils/poi-category';
import { parseReportSource } from './utils/report-source';
import { parseReportType } from './utils/report-type';
import { parseRideTheme } from './utils/ride-theme';

Chart.register(
  BarController,
  BarElement,
  LineController,
  PointElement,
  LineElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
);

Chart.defaults.font.family = '"Nunito Sans", "Roboto", sans-serif';

type TSmallDeviceShowDetailsAction = {
  labelKey: string;
  Icon: (props: SvgIconProps) => React.JSX.Element;
};

type TSmallDeviceRedirectAction = {
  callback: () => void;
  labelKey: string;
};

type TAppContext = {
  hasHeaderDarkBackground: boolean;
  headerBackgroundColor?: string;
  highlightedEvents?: Event[] | null;
  isDetailsView: boolean;
  siteUrl?: string | null;
  smallDeviceContentShowed: boolean;
  smallDeviceRedirectAction?: TSmallDeviceRedirectAction;
  smallDeviceShowDetailsAction: TSmallDeviceShowDetailsAction;
  mode: 'light' | 'dark';
};

type TMapContext = {
  baseLayer: TBaseLayer;
  bounds?: IBounds;
  current: Map | null;
  facilitiesShowed: boolean;
  heatmapShowed: boolean;
  hillshadesShowed: boolean;
  initialBounds?: LngLatBoundsLike;
  layersDrawerOpen: boolean;
  parkingSpacesShowed: boolean;
  reportsShowed: boolean;
  zoom?: number;
};

type TGeoaggloContext = {
  current: Geoagglo | null;
  all?: Geoagglo[];
  cities?: Geoagglo[];
  countries?: Geoagglo[];
};

type TPoiContext = {
  categories?: PoiCategory[];
  selectedCategories: { [key in TPoiCategoryCode]?: boolean };
};

export type TReportTypesMap = {
  [key in TReportTypeCode]?: IReportTypesMapValue & { id: number; titleKey: string };
};

export type TReportSourcesMap = {
  [key: number]: ReportSource;
};

type TReportContext = {
  newReportDialogOpen: boolean;
  sourcesMap?: TReportSourcesMap;
  typesMap?: TReportTypesMap;
};

type TUserContext = {
  allTimeH3Cells?: TH3Cells;
  bicycles?: TBicycle[];
  current?: User | null;
  commutingTrips?: CommutingTrip[];
  employees?: Array<{ employee: Employee; partner: Partner }>;
  gameLevel?: IUserGameLevel;
  geogroups?: UserGeogroup[];
  geolocation?: Place;
  loadingAllTimeH3Cells: boolean;
  places?: UserPlace[];
  routes?: Route[];
  rides?: UserRide[];
  signingOut: boolean;
  signingOutFailed?: boolean;
  trips?: Trip[];
};

type TGeogroupContext = {
  list?: Geogroup[];
};

type TRideContext = {
  themes?: RideTheme[];
  map: { [key: number]: Ride };
  bikeRoutes?: BikeRoute[];
};

type TActions = {
  getGeogroups: () => void;
  getUserAllTimeH3Cells: () => void;
  getUserRides: () => void;
  getUserRoutes: () => void;
  getUserTrips: () => void;
  getUserGeogroups: () => Promise<void>;
  openMapLayersDrawer: (open: boolean) => void;
  openNewReportDialog: (open: boolean) => void;
  selectPoiCategories: (categories: { [key in TPoiCategoryCode]?: boolean }) => void;
  setCurrentGeoagglo: (geoagglo: Geoagglo | null) => void;
  setCurrentUser: (user?: User | null) => void;
  setHasHeaderDarkBackground: (dark: boolean) => void;
  setHeaderBackgroundColor: (color?: string) => void;
  setIsDetailsView: (isDetails: boolean) => void;
  setMap: (map: Map | null) => void;
  setMapBaseLayer: (layer: TBaseLayer) => void;
  setMapBounds: (bounds?: IBounds) => void;
  setMapInitialBounds: (bounds: LngLatBoundsLike) => void;
  setMapZoom: (zoom?: number) => void;
  setRidesMap: (map: { [key: number]: Ride }) => void;
  setSmallDeviceShowDetailsAction: (action: TSmallDeviceShowDetailsAction) => void;
  setSmallDeviceRedirectAction: (action?: TSmallDeviceRedirectAction) => void;
  setUserBicycles: (bicycles: TBicycle[]) => void;
  setUserPlaces: (places?: UserPlace[]) => void;
  setUserRides: (rides?: UserRide[]) => void;
  setUserRoutes: (routes?: Route[]) => void;
  setUserTrips: (trips?: Trip[]) => void;
  setUserGeogroups: (geogroups?: UserGeogroup[]) => void;
  setUserCommutingTrips: (commutingTrips?: CommutingTrip[]) => void;
  showSmallDeviceContent: (showed: boolean) => void;
  signOut: () => void;
  toggleFacilitiesOnMap: (showed: boolean) => void;
  toggleHeatmapOnMap: (showed: boolean) => void;
  toggleHillshadesOnMap: (showed: boolean) => void;
  toggleParkingSpacesOnMap: (showed: boolean) => void;
  toggleReportsOnMap: (showed: boolean) => void;
  setMode: (mode: 'light' | 'dark') => void;
};

type TContext = {
  actions: TActions;
  app: TAppContext;
  geoagglo: TGeoaggloContext;
  geogroup: TGeogroupContext;
  map: TMapContext;
  poi: TPoiContext;
  report: TReportContext;
  ride: TRideContext;
  user: TUserContext;
};

export const mapPadding: PaddingOptions = { top: 64, right: 70, bottom: 78, left: 20 };

export const defaultSmallDeviceShowDetailsAction = {
  labelKey: 'commons.actions.hide_map',
  Icon: HideMapIcon,
};

export const AppContext = createContext<TContext>({
  app: {
    hasHeaderDarkBackground: false,
    isDetailsView: false,
    smallDeviceContentShowed: false,
    smallDeviceShowDetailsAction: defaultSmallDeviceShowDetailsAction,
    mode: 'light',
  },
  map: {
    current: null,
    baseLayer: 'geovelo',
    parkingSpacesShowed: true,
    reportsShowed: true,
    facilitiesShowed: false,
    hillshadesShowed: false,
    heatmapShowed: false,
    layersDrawerOpen: false,
  },
  geoagglo: { current: null },
  poi: { selectedCategories: {} },
  report: { newReportDialogOpen: false },
  user: { signingOut: false, loadingAllTimeH3Cells: false },
  geogroup: {},
  ride: { map: {} },
  actions: {
    setHasHeaderDarkBackground: () => undefined,
    setHeaderBackgroundColor: () => undefined,
    setIsDetailsView: () => undefined,
    showSmallDeviceContent: () => undefined,
    setSmallDeviceShowDetailsAction: () => undefined,
    setSmallDeviceRedirectAction: () => undefined,
    setMap: () => undefined,
    setMapInitialBounds: () => undefined,
    setMapBounds: () => undefined,
    setMapZoom: () => undefined,
    setMapBaseLayer: () => undefined,
    toggleParkingSpacesOnMap: () => undefined,
    toggleReportsOnMap: () => undefined,
    toggleFacilitiesOnMap: () => undefined,
    toggleHeatmapOnMap: () => undefined,
    toggleHillshadesOnMap: () => undefined,
    openMapLayersDrawer: () => undefined,
    setCurrentGeoagglo: () => undefined,
    selectPoiCategories: () => undefined,
    openNewReportDialog: () => undefined,
    setCurrentUser: () => undefined,
    setUserPlaces: () => undefined,
    getUserRoutes: () => undefined,
    setUserRoutes: () => undefined,
    getUserRides: () => undefined,
    setUserRides: () => undefined,
    getUserTrips: () => undefined,
    setUserTrips: () => undefined,
    getUserGeogroups: async () => await undefined,
    setUserGeogroups: () => undefined,
    setUserCommutingTrips: () => undefined,
    signOut: () => undefined,
    getGeogroups: () => undefined,
    setRidesMap: () => undefined,
    setMode: () => undefined,
    getUserAllTimeH3Cells: () => undefined,
    setUserBicycles: () => undefined,
  },
});

function ContextProvider({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { site, allPoiCategory, allReportType, allReportSource, allRideTheme, allGeoagglo } =
    useStaticQuery<ContextQuery>(graphql`
      query Context {
        site {
          siteMetadata {
            siteUrl
          }
        }
        allPoiCategory {
          nodes {
            data {
              code
              description
              id
              parent
              title
            }
          }
        }
        allReportType {
          nodes {
            data {
              code
              id
              is_bind_to_osm
            }
          }
        }
        allReportSource {
          nodes {
            originalId
            data {
              code
              id
              title
              url
            }
          }
        }
        allRideTheme {
          nodes {
            data {
              created
              description
              id
              title
              updated
            }
          }
        }
        allGeoagglo {
          nodes {
            data {
              area
              bounds
              code
              geo_center {
                coordinates
                type
              }
              has_bss
              has_cyclability
              has_public_transport
              has_rides
              id
              importance
              logo
              logo_alt
              long_name
              order
              photo
              short_name
            }
          }
        }
      }
    `);

  const [initialized, setInitialized] = useState(typeof window === 'undefined');
  const { trackEvent } = useTracker();
  const { get: getQueryParams } = useQueryParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  // app context
  const [hasHeaderDarkBackground, setHasHeaderDarkBackground] = useState(false);
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState<string>();
  const [isDetailsView, setIsDetailsView] = useState(false);
  const [smallDeviceContentShowed, showSmallDeviceContent] = useState(false);
  const [smallDeviceShowDetailsAction, setSmallDeviceShowDetailsAction] = useState(
    defaultSmallDeviceShowDetailsAction,
  );
  const [smallDeviceRedirectAction, setSmallDeviceRedirectAction] =
    useState<TSmallDeviceRedirectAction>();
  const [highlightedEvents, setHighlightedEvents] = useState<Event[]>();
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // map context
  const [map, setMap] = useState<Map | null>(null);
  const [mapInitialBounds, setMapInitialBounds] = useState<LngLatBoundsLike>();
  const [mapBounds, setMapBounds] = useState<IBounds>();
  const [mapZoom, setMapZoom] = useState<number>();
  const [mapBaseLayer, setMapBaseLayer] = useState<TBaseLayer>('geovelo');
  const [facilitiesShowedOnMap, toggleFacilitiesOnMap] = useState(() => {
    let facilitiesItem: string | null = null;
    try {
      if (typeof window !== 'undefined')
        facilitiesItem = localStorage.getItem('display_facilities');
    } catch {
      console.error('localStorage access is denied');
    }

    return facilitiesItem !== 'false';
  });
  const [hillshadesShowedOnMap, toggleHillshadesOnMap] = useState(() => {
    let hillshadesItem: string | null = null;
    try {
      if (typeof window !== 'undefined')
        hillshadesItem = localStorage.getItem('display_hillshades');
    } catch {
      console.error('localStorage access is denied');
    }

    return hillshadesItem === 'true';
  });
  const [heatmapShowedOnMap, toggleHeatmapOnMap] = useState(false);
  const [parkingSpacesShowedOnMap, toggleParkingSpacesOnMap] = useState<boolean>(() => {
    let parkingSpacesItem: string | null = null;
    try {
      if (typeof window !== 'undefined')
        parkingSpacesItem = localStorage.getItem('display_parking_spaces');
    } catch {
      console.error('localStorage access is denied');
    }

    return parkingSpacesItem === 'true';
  });
  const [reportsShowedOnMap, toggleReportsOnMap] = useState<boolean>(() => {
    let reportsItem: string | null = null;
    try {
      if (typeof window !== 'undefined') reportsItem = localStorage.getItem('display_reports');
    } catch {
      console.error('localStorage access is denied');
    }

    return reportsItem === 'true';
  });
  const [mapLayersDrawerOpen, openMapLayersDrawer] = useState(false);

  // geoagglo context
  const [geoagglos] = useState(
    allGeoagglo.nodes.reduce<Geoagglo[]>((res, { data }) => {
      if (data) res.push(parseGeoagglo(data));

      return res;
    }, []),
  );
  const [currentGeoagglo, setCurrentGeoagglo] = useState<Geoagglo | null>(null);

  // report context
  const [newReportDialogOpen, openNewReportDialog] = useState(false);
  const [reportTypes] = useState(() => {
    const _reportTypesMap = allReportType.nodes.reduce<{
      [code in TReportTypeCodeWithOthers]?: ReportType;
    }>((res, typeData) => {
      const type = typeData.data ? parseReportType(typeData.data) : null;
      if (type) res[type.code] = type;

      return res;
    }, {});

    return reportParentsCodes.map((parentKey) => {
      const { isBindToOSM, children } = reportTypesChildren[parentKey];

      return new ReportType(
        _reportTypesMap[parentKey]?.id || -1,
        parentKey,
        isBindToOSM,
        reportTypesTitleKeys[parentKey],
        children.reduce<ReportType[]>((res, childKey) => {
          const child = _reportTypesMap[childKey];
          if (child) res.push(child);

          return res;
        }, []),
      );
    });
  });
  const [reportTypesMap] = useState<TReportTypesMap>(
    reportTypes
      .flatMap<ReportType>((reportType) => [reportType, ...reportType.children])
      .reduce<TReportTypesMap>((res, { id, code, titleKey }) => {
        if (!reportTypeOthers.includes(code)) {
          res[code as TReportTypeCode] = {
            id,
            ..._reportTypesMap[code as TReportTypeCode],
            titleKey,
          };
        }
        return res;
      }, {}),
  );
  const [reportSourcesMap] = useState<TReportSourcesMap>(
    allReportSource.nodes.reduce<TReportSourcesMap>((res, { data }) => {
      if (data?.id) {
        const source = parseReportSource(data);
        if (source) res[data.id] = source;
      }

      return res;
    }, {}),
  );

  // poi context
  const [poiCategories] = useState(
    parsePoiCategories(
      allPoiCategory.nodes.reduce<PoiCategoryData[]>((res, { data }) => {
        if (data) res.push(data);

        return res;
      }, []),
    ),
  );
  const [selectedPoiCategories, selectPoiCategories] = useState<{
    [key in TPoiCategoryCode]?: boolean;
  }>(() => {
    const _selectedCategories: { [key in TPoiCategoryCode]?: boolean } = {};
    let item: string | null = null;
    try {
      if (typeof window !== 'undefined') item = localStorage.getItem('poi_categories');
    } catch {
      console.error('localStorage access is denied');
    }
    const _poiCategoryCodes = item ? item.split(',') : [];

    poiCategoryCodes.forEach((code) => {
      if (_poiCategoryCodes.indexOf(code) > -1)
        _selectedCategories[code as TPoiCategoryCode] = true;
    });

    return _selectedCategories;
  });

  // user context
  const [currentUser, setCurrentUser] = useState<User | null>();
  const [userGeolocation, setUserGeolocation] = useState<Place>();
  const [userPlaces, setUserPlaces] = useState<UserPlace[]>();
  const [userRoutes, setUserRoutes] = useState<Route[]>();
  const [userRides, setUserRides] = useState<UserRide[]>();
  const [userTrips, setUserTrips] = useState<Trip[]>();
  const [userGeogroups, setUserGeogroups] = useState<UserGeogroup[]>();
  const [userEmployees, setUserEmployees] =
    useState<Array<{ employee: Employee; partner: Partner }>>();
  const [userCommutingTrips, setUserCommutingTrips] = useState<CommutingTrip[]>();
  const [userGameLevel, setUserGameLevel] = useState<IUserGameLevel>();
  const [userBicycles, setUserBicycles] = useState<TBicycle[]>();
  const [signingOut, setSigningOut] = useState(false);
  const [signingOutFailed, setSigningOutFailed] = useState<boolean>();
  const [allTimeH3Cells, setAllTimeH3Cells] = useState<TH3Cells>();
  const [loadingAllTimeH3Cells, setLoadingAllTimeH3Cells] = useState(false);
  const loadingAllTimeSimplifiedTracesRef = useRef(false);
  const loadingUserBicycles = useRef(false);
  const { cancellablePromise: cancellableUserPromise, cancelPromises: cancelUserPromises } =
    useCancellablePromise();

  // geogroup context
  const [geogroups, setGeogroups] = useState<Geogroup[]>();

  // ride context
  const [rideThemes] = useState(
    allRideTheme.nodes.reduce<RideTheme[]>((res, { data }) => {
      if (data) {
        const theme = parseRideTheme(data);
        if (theme) res.push(theme);
      }

      return res;
    }, []),
  );
  const [ridesMap, setRidesMap] = useState<{ [key: number]: Ride }>({});

  useEffect(() => {
    init();

    let watchPositionId: number;
    if ('geolocation' in navigator) {
      let id: number;
      watchPositionId = navigator.geolocation.watchPosition(
        async ({ coords: { latitude: lat, longitude: lng } }) => {
          id = new Date().getTime();
          const point: GeoJSON.Point = { type: 'Point', coordinates: [lng, lat] };

          try {
            const place = await GeocoderService.reverseGeocode(id, point);
            if (place.id === id) {
              place.id = 'geolocation';
              setUserGeolocation(place);
            }
          } catch {
            setUserGeolocation(new Place(id, point));
          }

          navigator.geolocation.clearWatch(watchPositionId);
        },
        (err) => console.error(err),
      );
    }

    return () => {
      navigator.geolocation.clearWatch(watchPositionId);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackEvent('Page Visited', { pathname: window.location.pathname });
    }
  }, [typeof window !== 'undefined' && window.location.pathname]);

  useEffect(() => {
    if (currentUser) {
      getUserPlaces();
      getUserEmployee();
      getUserGameLevel();
      if (environment.features.equipment === 'all' || currentUser.isGeovelo) getUserBicycles();
    } else {
      setUserPlaces(undefined);
      setUserRoutes(undefined);
      setUserRides(undefined);
      setUserTrips(undefined);
      setUserGeogroups(undefined);
      setUserEmployees(undefined);
      setUserCommutingTrips(undefined);
      setUserGameLevel(undefined);
      setAllTimeH3Cells(undefined);
      setUserBicycles(undefined);
    }

    return () => cancelUserPromises();
  }, [currentUser]);

  async function init() {
    AppService.environment = environment;
    const { highlightedEventIds } = environment;

    if (highlightedEventIds && highlightedEventIds.length > 0)
      getHighlightedEvents(highlightedEventIds);

    const clientId = getQueryParams()['client-id'];
    const userId = getQueryParams()['user-id'];
    const authorizationToken = getQueryParams()['auth-token'];

    clearQueryParam('user-id');
    clearQueryParam('auth-token');

    if (clientId) {
      HttpService.authorizationToken = null;

      try {
        localStorage.removeItem('impersonate_authorization_token');
        localStorage.removeItem('impersonate_user_id');
        localStorage.removeItem('authorization_token');
        localStorage.removeItem('user_id');
        sessionStorage.removeItem('authorization_token');
        sessionStorage.removeItem('user_id');
      } catch {
        console.error('localStorage access is denied');
      }

      setCurrentUser(null);
    } else {
      if (userId && authorizationToken) {
        try {
          HttpService.authorizationToken = `Token ${authorizationToken}`;
          localStorage.setItem('authorization_token', `Token ${authorizationToken}`);
          localStorage.setItem('user_id', `${userId}`);
        } catch {
          console.error('localStorage access is denied');
        }
      }

      getCurrentUser();
    }

    setInitialized(true);
  }

  async function getHighlightedEvents(ids: number[]) {
    try {
      const events = await Promise.all(ids.map((id) => EventService.getEvent(id)));

      setHighlightedEvents(events);
    } catch (err) {
      console.error(err);
    }
  }

  async function getCurrentUser() {
    try {
      const user = await UserService.getCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      console.error(err);
    }
  }

  async function getUserPlaces() {
    try {
      const places = await cancellableUserPromise(UserService.getPlaces());
      setUserPlaces(
        places.sort((a, b) => {
          if (a.type === 'home' && b.type !== 'home') return -1;
          if (b.type === 'home' && a.type !== 'home') return 1;
          if (a.type === 'work' && b.type !== 'work') return -1;
          if (b.type === 'work' && a.type !== 'work') return 1;

          return a.created.isBefore(b.created) ? -1 : 1;
        }),
      );
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  async function getUserEmployee() {
    try {
      const employees = await cancellableUserPromise(EmployeeService.getEmployees());

      setUserEmployees(employees);
      if (employees.length > 0)
        await getUserCommutingTrips(employees.map(({ employee }) => employee));
      else setUserCommutingTrips([]);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  async function getUserCommutingTrips(employees: Employee[]) {
    try {
      const commutingTrips = (
        await cancellableUserPromise(
          Promise.all(
            employees.map(({ id: employeeId }) =>
              CommutingTripService.getCommutingTrips({ employeeId }),
            ),
          ),
        )
      ).flatMap((commutingTrips) => commutingTrips);

      setUserCommutingTrips(commutingTrips);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setUserCommutingTrips([]);
      }
    }
  }

  async function getUserGameLevel() {
    try {
      const result = await cancellableUserPromise(UserService.getUserGameLevel());
      setUserGameLevel(result);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  async function getUserRoutes() {
    try {
      const routes = await RouteService.getRoutes({ types: ['USER'] });
      setUserRoutes(routes);
    } catch (err) {
      console.error(err);
    }
  }

  async function getUserRides() {
    try {
      const _userRides = await UserService.getRides();

      if (_userRides.length > 0) {
        const { rides } = await RideService.getRides({
          page: 1,
          pageSize: _userRides.length,
          ids: _userRides.map(({ rideId }) => rideId),
          query:
            '{id, title, geo_point_a, geo_point_a_title, geo_point_b, geo_point_b_title, geo_point_center, duration, vertical_gain, route_duration, distance, difficulty, icon, photos{thumbnail, thumbnailSquare}}',
        });

        const _ridesMap = rides.reduce<{ [key: number]: Ride }>((res, ride) => {
          if (ride.id) res[ride.id] = ride;

          return res;
        }, {});

        setRidesMap(_ridesMap);
        setUserRides(_userRides.filter(({ rideId }) => _ridesMap[rideId]));
      } else setUserRides([]);
    } catch (err) {
      console.error(err);
    }
  }

  async function getUserTrips() {
    try {
      const trips = await TripService.getUserTrips({
        query: '{id, title, distance, steps{id, itinerary{geometry}}}',
      });
      setUserTrips(trips.sort((a, b) => b.id - a.id));
    } catch (err) {
      console.error(err);
    }
  }

  async function getUserGeogroups() {
    try {
      const geogroups = await GeogroupService.getUserGeogroups();

      setUserGeogroups(geogroups.filter(({ group: { code } }) => code !== 'france') || []);
    } catch (err) {
      console.error(err);
    }
  }

  async function signOut() {
    setSigningOut(true);
    setSigningOutFailed(undefined);

    try {
      await AuthService.signOut();
      trackEvent('Sign Out Button Clicked', { context: 'Profile Menu' });
      setCurrentUser(null);
    } catch {
      setSigningOutFailed(true);
    }

    setSigningOut(false);
  }

  async function getGeogroups() {
    try {
      const { geogroups } = await GeogroupService.getGeogroups();

      setGeogroups(geogroups);
    } catch (err) {
      console.error(err);
      setGeogroups([]);
    }
  }

  async function getUserAllTimeH3Cells() {
    if (!currentUser || loadingAllTimeSimplifiedTracesRef.current || allTimeH3Cells) return;

    setLoadingAllTimeH3Cells(true);

    try {
      const cells = await UserService.getH3Cells();

      setAllTimeH3Cells(cells);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }

    loadingAllTimeSimplifiedTracesRef.current = false;
    setLoadingAllTimeH3Cells(false);
  }

  async function getUserBicycles() {
    if (!currentUser || loadingUserBicycles.current) return;

    try {
      const bicycles = await cancellableUserPromise(EquipmentService.getBicycles());

      setUserBicycles(bicycles.sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0)));
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }

    loadingUserBicycles.current = false;
  }

  if (!initialized) return <></>;

  return (
    <AppContext.Provider
      value={{
        app: {
          siteUrl: site?.siteMetadata?.siteUrl,
          hasHeaderDarkBackground,
          headerBackgroundColor,
          isDetailsView,
          smallDeviceContentShowed,
          smallDeviceShowDetailsAction,
          smallDeviceRedirectAction,
          highlightedEvents,
          mode,
        },
        map: {
          current: map,
          initialBounds: mapInitialBounds,
          bounds: mapBounds,
          zoom: mapZoom,
          baseLayer: mapBaseLayer,
          parkingSpacesShowed: parkingSpacesShowedOnMap,
          reportsShowed: reportsShowedOnMap,
          facilitiesShowed: facilitiesShowedOnMap,
          hillshadesShowed: hillshadesShowedOnMap,
          heatmapShowed: heatmapShowedOnMap,
          layersDrawerOpen: mapLayersDrawerOpen,
        },
        geoagglo: {
          all: geoagglos,
          cities: geoagglos?.filter(({ code }) => countryCodes.indexOf(code) === -1),
          countries: geoagglos?.filter(({ code }) => countryCodes.indexOf(code) > -1),
          current: currentGeoagglo,
        },
        poi: { categories: poiCategories, selectedCategories: selectedPoiCategories },
        report: {
          newReportDialogOpen,
          typesMap: reportTypesMap,
          sourcesMap: reportSourcesMap,
        },
        user: {
          current: currentUser,
          places: userPlaces,
          routes: userRoutes,
          rides: userRides,
          trips: userTrips,
          geogroups: userGeogroups,
          geolocation: userGeolocation,
          employees: userEmployees,
          commutingTrips: userCommutingTrips,
          gameLevel: userGameLevel,
          bicycles: userBicycles,
          signingOut,
          signingOutFailed,
          allTimeH3Cells,
          loadingAllTimeH3Cells,
        },
        geogroup: {
          list: geogroups,
        },
        ride: { themes: rideThemes, map: ridesMap },
        actions: {
          setHasHeaderDarkBackground,
          setHeaderBackgroundColor,
          setIsDetailsView,
          showSmallDeviceContent,
          setSmallDeviceShowDetailsAction,
          setSmallDeviceRedirectAction,
          setMap,
          setMapInitialBounds,
          setMapBounds,
          setMapZoom,
          setMapBaseLayer,
          toggleParkingSpacesOnMap,
          toggleReportsOnMap,
          toggleFacilitiesOnMap,
          toggleHeatmapOnMap,
          toggleHillshadesOnMap,
          openMapLayersDrawer,
          setCurrentGeoagglo,
          selectPoiCategories,
          openNewReportDialog,
          setCurrentUser,
          setUserPlaces,
          getUserRoutes,
          setUserRoutes,
          getUserRides,
          setUserRides,
          getUserTrips,
          setUserTrips,
          getUserGeogroups,
          setUserGeogroups,
          setUserCommutingTrips,
          setUserBicycles,
          signOut,
          getGeogroups,
          setRidesMap,
          setMode,
          getUserAllTimeH3Cells,
        },
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export default ContextProvider;
